import { useState, useCallback } from 'react';
import { 
  normalizeDate, 
  collectDatesFromAccount, 
  createBaseTotalItem,
  getSubcontoTypes
} from '../utils';

export const useAccountsState = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [accountsData, setAccountsData] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);

  const normalizeAccounts = useCallback((accountsArray) => {
    return accountsArray.map((account, index) => ({
      ...account,
      title: account.title || `Account ${index}`,
      values: (account.values || []).map(subAccount => ({
        ...subAccount,
        title: subAccount.title || 'Sub-account',
        values: (subAccount.values || []).map(contractor => ({ 
          ...contractor, 
          title: contractor.title || 'Contractor' 
        }))
      }))
    }));
  }, []);

  const updateAccountsData = useCallback((newAccountsData) => {
    setAccountsData(newAccountsData);
    setAccounts(newAccountsData.map(acc => ({ title: acc.title })));
    
    // Update available dates from the first account
    if (newAccountsData.length > 0) {
      const firstAccount = newAccountsData[0];
      const sortedDates = collectDatesFromAccount(firstAccount);
      setAvailableDates(sortedDates);
      
      if (!selectedAccountId) {
        setSelectedAccountId(firstAccount.title);
      }
    }
  }, [selectedAccountId]);

  const addAccount = useCallback((title, copyFrom = null) => {
    const newAccount = copyFrom ? 
      { ...JSON.parse(JSON.stringify(copyFrom)), title } :
      { title, inn: "", values: [], total: [] };
    
    setAccountsData(prev => {
      const updated = [...prev, newAccount];
      setAccounts(updated.map(acc => ({ title: acc.title })));
      setSelectedAccountId(title);
      return updated;
    });
  }, []);

  const deleteAccount = useCallback((accountTitle) => {
    setAccountsData(prev => {
      const updated = prev.filter(acc => acc.title !== accountTitle);
      setAccounts(updated.map(acc => ({ title: acc.title })));
      setSelectedAccountId(updated.length > 0 ? updated[0].title : "");
      return updated;
    });
  }, []);

  const addSubAccount = useCallback((title) => {
    const newSubAccount = {
      title: title.trim(),
      values: [],
      isNew: true,
      total: availableDates.map(date => createBaseTotalItem(date))
    };
    
    setAccountsData(prev => {
      return prev.map(account => {
        if (account.title === selectedAccountId) {
          return { ...account, values: [...(account.values || []), newSubAccount] };
        }
        return account;
      });
    });
  }, [selectedAccountId, availableDates]);

  const deleteSubAccount = useCallback((subIndex) => {
    setAccountsData(prev => {
      return prev.map(account => {
        if (account.title === selectedAccountId) {
          const updatedValues = [...(account.values || [])];
          updatedValues.splice(subIndex, 1);
          return { ...account, values: updatedValues };
        }
        return account;
      });
    });
  }, [selectedAccountId]);

  const addCounterparty = useCallback((subIndex, title) => {
    setAccountsData(prev => {
      return prev.map(account => {
        if (account.title === selectedAccountId) {
          const updatedValues = [...(account.values || [])];
          if (updatedValues[subIndex]) {
            const { subconto1Type, subconto2Type } = getSubcontoTypes(account);
            
            const subconto = [
              { value: title.trim() },
              { value: "" }
            ];

            if (subconto1Type) {
              subconto[0].type = subconto1Type;
            }
            if (subconto2Type) {
              subconto[1].type = subconto2Type;
            }

            const newCounterparty = {
              title: title.trim(),
              inn: "",
              subconto: subconto,
              isNew: true,
              values: availableDates.map(date => createBaseTotalItem(date))
            };
            
            updatedValues[subIndex] = {
              ...updatedValues[subIndex],
              values: [...(updatedValues[subIndex].values || []), newCounterparty]
            };
          }
          return { ...account, values: updatedValues };
        }
        return account;
      });
    });
  }, [selectedAccountId, availableDates]);

  const deleteCounterparty = useCallback((subIndex, cpIndex) => {
    setAccountsData(prev => {
      return prev.map(account => {
        if (account.title === selectedAccountId) {
          const updatedValues = [...(account.values || [])];
          if (updatedValues[subIndex] && updatedValues[subIndex].values) {
            const updatedCounterparties = [...updatedValues[subIndex].values];
            updatedCounterparties.splice(cpIndex, 1);
            updatedValues[subIndex] = {
              ...updatedValues[subIndex],
              values: updatedCounterparties
            };
          }
          return { ...account, values: updatedValues };
        }
        return account;
      });
    });
  }, [selectedAccountId]);

  const deleteDate = useCallback((dateToDelete) => {
    setAccountsData(prev => {
      return prev.map(account => {
        if (account.title === selectedAccountId) {
          return {
            ...account,
            total: (account.total || []).filter(t => normalizeDate(t.date || t.Date) !== dateToDelete),
            values: (account.values || []).map(subAccount => ({
              ...subAccount,
              total: (subAccount.total || []).filter(t => normalizeDate(t.date || t.Date) !== dateToDelete),
              values: (subAccount.values || []).map(counterparty => ({
                ...counterparty,
                values: (counterparty.values || []).filter(v => normalizeDate(v.date || v.Date) !== dateToDelete)
              }))
            }))
          };
        }
        return account;
      });
    });
    
    setAvailableDates(prev => prev.filter(date => date !== dateToDelete));
  }, [selectedAccountId]);

  const addDate = useCallback((newDate) => {
    setAccountsData(prev => {
      return prev.map(account => {
        if (account.title === selectedAccountId) {
          return {
            ...account,
            total: [...(account.total || []), createBaseTotalItem(newDate)],
            values: (account.values || []).map(subAccount => ({
              ...subAccount,
              total: [...(subAccount.total || []), createBaseTotalItem(newDate)],
              values: (subAccount.values || []).map(contractorItem => ({
                ...contractorItem,
                values: [...(contractorItem.values || []), createBaseTotalItem(newDate)]
              }))
            }))
          };
        }
        return account;
      });
    });
    
    setAvailableDates(prev => [...prev, newDate].sort());
  }, [selectedAccountId]);

  const getSelectedAccount = useCallback(() => {
    return accountsData.find(acc => acc.title === selectedAccountId);
  }, [accountsData, selectedAccountId]);

  return {
    accounts,
    selectedAccountId,
    setSelectedAccountId,
    accountsData,
    setAccountsData,
    availableDates,
    setAvailableDates,
    normalizeAccounts,
    updateAccountsData,
    addAccount,
    deleteAccount,
    addSubAccount,
    deleteSubAccount,
    addCounterparty,
    deleteCounterparty,
    deleteDate,
    addDate,
    getSelectedAccount
  };
};